import {
  CalendarContainer,
  CalendarContainerContext
} from '@library/components/datePicker/CalendarContainer';
import { DateTime } from 'luxon';
import { PrizepicksDatePicker } from '@library/components/datePicker';
import type { ReactNode } from 'react';
import { TimePickerContext } from '@library/components/datePicker/TimePickerContext';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { useCalendarContainer } from '@library/hooks/useCalendarContainer';
import { useCallback } from 'react';
import { useTimePickerContext } from '@library/hooks/useTimePickerContext';

export interface ActionDatePickerProps {
  changeTime: (date: DateTime) => void;
  customInput: ReactNode;
  initialTime: DateTime;
  maxDate?: DateTime;
  minDate?: DateTime;
  timeOpen: boolean;
  setTimeOpen: (open: boolean) => void;
  uniqueStartTimes?: Array<string>;
}

export const ActionDatePicker = ({
  changeTime,
  customInput,
  initialTime,
  maxDate = undefined,
  minDate = undefined,
  setTimeOpen,
  timeOpen,
  uniqueStartTimes = undefined
}: ActionDatePickerProps) => {
  const timePickerContext = useTimePickerContext({
    initialDate: initialTime,
    maxDate,
    minDate,
    uniqueStartTimes
  });

  const resetTime = useCallback(
    (date: DateTime) => {
      timePickerContext.setInitialTime(date);

      timePickerContext.resetTime();
    },
    [timePickerContext]
  );

  const handleSaveTime = useCallback(
    (date: DateTime) => {
      changeTime(date);
      setTimeOpen(false);

      resetTime(date);
    },
    [changeTime, resetTime, setTimeOpen]
  );

  const calendarContainerContext = useCalendarContainer({
    handleSave: handleSaveTime,
    proposedTime: timePickerContext.proposedTime,
    resetTime: timePickerContext.resetTime,
    setTimeOpen,
    time: initialTime,
    validations: timePickerContext.validations
  });

  const handleChooseDate = (date: Date) => {
    const newDate = DateTime.fromJSDate(date);

    const newDateObject = {
      day: newDate.day,
      month: newDate.month,
      year: newDate.year
    };

    const newProposedDate = timePickerContext.proposedTime.set(newDateObject);

    timePickerContext.setProposedTime(newProposedDate);

    timePickerContext.validateTime({
      inputName: 'hour',
      newTime: newProposedDate,
      value: formatWithTimezoneChange(newProposedDate, 'h')
    });
  };

  return (
    <TimePickerContext.Provider value={timePickerContext}>
      <CalendarContainerContext.Provider value={calendarContainerContext}>
        <PrizepicksDatePicker
          calendarContainer={CalendarContainer}
          closeOnSelect={false}
          customInput={customInput}
          maxDate={maxDate && new Date(formatWithTimezoneChange(maxDate, 'D'))}
          minDate={minDate && new Date(formatWithTimezoneChange(minDate, 'D'))}
          monthsShown={1}
          handleChange={handleChooseDate}
          onCalendarOpen={() => resetTime(initialTime)}
          open={timeOpen}
          setOpen={setTimeOpen}
          value={
            new Date(
              formatWithTimezoneChange(timePickerContext.proposedTime, 'D')
            )
          }
        />
      </CalendarContainerContext.Provider>
    </TimePickerContext.Provider>
  );
};
